import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  Flex,
  Menu,
  MenuDivider,
  MenuItem,
  MenuList,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from "@chakra-ui/react";
import { EditIcon } from "@chakra-ui/icons";
import { gql } from "__generated__/gql";
import type {
  RetailerLocationGroupFilter,
  RetailerLocationWithIdentifierFilter,
} from "__generated__/gql/graphql";
import { useQuery } from "utils/apollo/hooks";
import { ADMIN_CONTEXT } from "utils/apollo/hooks/admin";
import { useActiveBrandIdContext } from "utils/apollo/hooks/brand";
import { useCurrentBrandId } from "utils/currentUserHooks";
import { formatInteger } from "utils/formatting";
import { INVALID_UUID } from "utils/misc";
import MoreMenuButton from "components/Shared/MoreMenuButton";
import { NameWithFlagIcons } from "components/Shared/RetailerDirectory/utils";
import { FREESTANDING_TABLE_CONTAINER_PROPS } from "components/Shared/ChakraReactTable/FullPageTable";
import {
  ADMIN_RETAILER_LOCATION_GROUPS_INDEX,
  ADMIN_RETAILER_LOCATIONS_INDEX,
} from "utils/links";
import CopyMenuItem from "components/Shared/CopyMenuItem";
import { APP_NAME } from "utils/config";
import LinkButton from "components/Shared/LinkButton";
import CreateButton from "components/Shared/CreateButton";
import MergeModalMenuItem from "components/Shared/MergeModal/MergeModalMenuItem";
import AdminRetailerBrandsTable from "components/Admin/Retailers/BrandsTable";
import RetailerDetails from "./Details";
import IsCustomBadge, { getIsRetailerCustom } from "./IsCustomBadge";
import EditRetailerDetails from "./Edit";
import ArchiveMenuItem from "./ArchiveMenuItem";
import DeleteMenuItem from "./DeleteMenuItem";
import MergeRetailer from "./MergeRetailer";
import RetailerLocationsDirectoryTable, {
  LOCATION_COLUMNS,
} from "./Locations/Table/Table";
import CreateRetailerLocation from "./Locations/Selected/Create";
import CreateRetailerLocationGroup from "./LocationGroups/Selected/Create";
import RetailerLocationGroupsTable, {
  LOCATION_GROUP_COLUMNS,
} from "./LocationGroups/Table/Table";
import RetailerMappings from "./Mappings";
import RetailerBrokerIcons from "./RetailerBrokerIcons";

const query = gql(/* GraphQL */ `
  query RetailerDirectorySelected(
    $id: UUID!
    $brandId: UUID!
    $isAdmin: Boolean = false
  ) {
    retailer(id: $id) {
      id
      forBrand @include(if: $isAdmin) {
        id
        name
      }
      brandRetailers @include(if: $isAdmin) {
        totalCount
      }
      retailerLocations(
        condition: { isArchived: false, isUnknown: false }
        orderBy: CREATED_AT_ASC
      ) @include(if: $isAdmin) {
        totalCount
      }
      retailerLocationGroups(orderBy: CREATED_AT_ASC) @include(if: $isAdmin) {
        totalCount
      }
      ...RetailerNameWithFlagIconsFragment
      ...RetailerBrokerIcons @skip(if: $isAdmin)
      ...RetailerIsCustomBadge
      ...RetailerDetails
      ...EditRetailerDetails
      ...RetailerArchiveMenuItemFragment
      ...RetailerDeleteMenuItemFragment
    }
  }
`);

/**
  We cache the number of locations for each retailer ID to avoid unmounting
  the table while adding a new location, which temporarily makes the current
  `numLocations` undefined while the query refreshes.
 */
function useNumLocations(
  numLocationsCurrent: number | undefined,
  selectedRetailerId: UUID | null,
) {
  const numLocationsByRetailerId = useRef<{ [retailerId: UUID]: number }>({});

  useEffect(() => {
    if (selectedRetailerId && typeof numLocationsCurrent === "number") {
      numLocationsByRetailerId.current[selectedRetailerId] =
        numLocationsCurrent;
    }
  }, [selectedRetailerId, numLocationsCurrent]);

  const numLocationsCached = selectedRetailerId
    ? numLocationsByRetailerId.current[selectedRetailerId]
    : undefined;

  return typeof numLocationsCurrent === "number"
    ? numLocationsCurrent
    : numLocationsCached;
}

type Props = {
  footer?: ReactNode;
  isAdmin?: boolean;
  isReadOnly?: boolean;
  onClose: () => void;
  onSelectedRetailerIdChange: (retailerId: UUID) => void;
  selectedRetailerId: UUID | null;
};

export default function RetailerDrawerContent({
  footer,
  isAdmin = false,
  isReadOnly = false,
  onClose,
  onSelectedRetailerIdChange,
  selectedRetailerId,
}: Props) {
  const [isEditing, setIsEditing] = useState(false);
  const [isAddingLocation, setIsAddingLocation] = useState(false);
  const [isAddingLocationGroup, setIsAddingLocationGroup] = useState(false);

  useEffect(() => {
    setIsEditing(false);
  }, [selectedRetailerId]);

  const brandId = useCurrentBrandId();
  const brandContext = useActiveBrandIdContext();

  const { data, loading } = useQuery(query, {
    context: isAdmin ? ADMIN_CONTEXT : brandContext,
    skip: !selectedRetailerId || (!isAdmin && !brandId),
    variables: selectedRetailerId
      ? { id: selectedRetailerId, brandId: brandId || INVALID_UUID, isAdmin }
      : undefined,
    returnPartialData: true,
    fetchPolicy: "cache-and-network",
  });

  const numLocations = useNumLocations(
    data?.retailer?.retailerLocations?.totalCount,
    selectedRetailerId,
  );

  const isCustom = data?.retailer && getIsRetailerCustom(data.retailer);
  const isEditable = !!data?.retailer && isCustom !== isAdmin;
  const hasLocations = !!numLocations;
  const numLocationGroups = data?.retailer?.retailerLocationGroups?.totalCount;
  const numBrands = data?.retailer?.brandRetailers?.totalCount;

  const locationTableFilter = useMemo<RetailerLocationWithIdentifierFilter>(
    () => ({
      retailerLocation: {
        retailerId: { equalTo: selectedRetailerId },
        isArchived: { equalTo: false },
        isUnknown: { equalTo: false },
      },
    }),
    [selectedRetailerId],
  );

  const locationGroupTableFilter = useMemo<RetailerLocationGroupFilter>(
    () => ({ retailerId: { equalTo: selectedRetailerId } }),
    [selectedRetailerId],
  );

  return (
    <DrawerContent bg="cardBackground">
      <DrawerHeader fontWeight="inherit">
        <Flex alignItems="center" gap={3}>
          <NameWithFlagIcons
            retailer={data?.retailer}
            display="inline-flex"
            fontWeight="semibold"
          >
            {data?.retailer && (
              <RetailerBrokerIcons retailer={data?.retailer} />
            )}
          </NameWithFlagIcons>

          {data?.retailer && (
            <IsCustomBadge retailer={data.retailer} fontWeight="semibold" />
          )}

          <Flex ml="auto" gap={2} alignSelf="flex-start">
            {!isReadOnly && (isAdmin || isCustom) && (
              <Menu>
                <MoreMenuButton
                  aria-label="Retailer Actions"
                  size="md"
                  px=".2em"
                  py=".2em"
                  isDisabled={isEditing}
                />
                <MenuList fontSize="md">
                  <MenuItem
                    icon={<EditIcon />}
                    onClick={() => setIsEditing(true)}
                    isDisabled={!isEditable}
                  >
                    Edit {isCustom ? "Custom" : "Default"} Retailer Details…
                  </MenuItem>

                  <CopyMenuItem value={selectedRetailerId}>
                    Copy Retailer ID
                  </CopyMenuItem>

                  <MenuDivider />

                  <MergeModalMenuItem
                    label={`Merge ${isCustom ? "Custom" : "Default"} Retailer`}
                    isDisabled={!data?.retailer || !(isAdmin || isCustom)}
                    size="4xl"
                    renderContent={(props) => {
                      if (!selectedRetailerId) return <>ERROR</>;
                      return (
                        <MergeRetailer
                          {...props}
                          isAdmin={isAdmin}
                          retailerId={selectedRetailerId}
                          onCompleted={() => onClose()}
                        />
                      );
                    }}
                  />

                  <MenuDivider />

                  <ArchiveMenuItem
                    isAdmin={isAdmin}
                    retailer={data?.retailer}
                    isDisabled={!isEditable}
                  />

                  <DeleteMenuItem
                    isAdmin={isAdmin}
                    isDisabled={!isEditable}
                    retailer={data?.retailer}
                    onCompleted={onClose}
                  />
                </MenuList>
              </Menu>
            )}
            <DrawerCloseButton position="static" isDisabled={isEditing} />
          </Flex>
        </Flex>

        {data?.retailer?.forBrand && (
          <Alert fontSize="sm" mt={3} p={2}>
            <AlertIcon />
            <AlertDescription lineHeight="normal">
              This is a custom retailer for the brand{" "}
              <strong>{data.retailer.forBrand.name}</strong>. To edit this
              retailer, please go to the brand’s {APP_NAME} instance.
            </AlertDescription>
          </Alert>
        )}
      </DrawerHeader>

      {isEditing ? (
        <EditRetailerDetails
          data={data?.retailer}
          isAdmin={isAdmin}
          isLoading={loading}
          onCloseEdit={() => setIsEditing(false)}
        />
      ) : (
        <>
          <DrawerBody display="flex" flexFlow="column" flex="1 1 100%">
            <Tabs
              isLazy
              size="sm"
              colorScheme="lightPinkButton"
              display="flex"
              flexFlow="column"
              flex="1 1 100%"
              minH={0}
            >
              {isAdmin && (
                <TabList>
                  <Tab>Details</Tab>

                  <Tab>
                    Locations
                    {hasLocations && (
                      <Badge
                        colorScheme="pinkButton"
                        variant="outline"
                        ml={2}
                        fontFamily="body"
                        lineHeight={1.5}
                      >
                        {formatInteger(numLocations)}
                      </Badge>
                    )}
                  </Tab>

                  <Tab>
                    Location Groups
                    {!!numLocationGroups && (
                      <Badge
                        colorScheme="pinkButton"
                        variant="outline"
                        ml={2}
                        fontFamily="body"
                        lineHeight={1.5}
                      >
                        {formatInteger(numLocationGroups)}
                      </Badge>
                    )}
                  </Tab>

                  <Tab>
                    Brands{" "}
                    {!!numBrands && numBrands > 0 && (
                      <Badge
                        colorScheme="pinkButton"
                        variant="outline"
                        ml={2}
                        lineHeight={1.5}
                      >
                        {formatInteger(numBrands)}
                      </Badge>
                    )}
                  </Tab>

                  <Tab>Mappings</Tab>
                </TabList>
              )}

              <TabPanels
                display="flex"
                flexFlow="column"
                flex="1 1 100%"
                minH={0}
              >
                <TabPanel px={0}>
                  <RetailerDetails
                    data={data?.retailer}
                    isAdmin={isAdmin}
                    isLoading={loading}
                    onSelectedRetailerIdChange={onSelectedRetailerIdChange}
                  />
                </TabPanel>

                {isAdmin && (
                  <TabPanel
                    px={0}
                    display="flex"
                    flexFlow="column"
                    flex="1 1 100%"
                    minH="25rem"
                  >
                    <Flex justifyContent="flex-end" gap={2} pb={3}>
                      {!isReadOnly && (
                        <CreateButton onClick={() => setIsAddingLocation(true)}>
                          Add Location
                        </CreateButton>
                      )}

                      <LinkButton
                        href={{
                          pathname: ADMIN_RETAILER_LOCATIONS_INDEX,
                          // eslint-disable-next-line @typescript-eslint/naming-convention
                          query: { retailer__retailer: selectedRetailerId },
                        }}
                        size="xs"
                        colorScheme="pinkButton"
                        variant="outline"
                      >
                        View in Locations ►
                      </LinkButton>
                    </Flex>

                    <RetailerLocationsDirectoryTable
                      defaultPageSize={25}
                      columns={LOCATION_COLUMNS}
                      isAdmin={isAdmin}
                      filter={locationTableFilter}
                      {...FREESTANDING_TABLE_CONTAINER_PROPS}
                      scrollContainerProps={{
                        ...FREESTANDING_TABLE_CONTAINER_PROPS.scrollContainerProps,
                        maxHeight: undefined,
                        minHeight: undefined,
                        bg: "gray.50",
                      }}
                    />
                  </TabPanel>
                )}

                {isAdmin && (
                  <TabPanel
                    px={0}
                    display="flex"
                    flexFlow="column"
                    flex="1 1 100%"
                    minH="25rem"
                  >
                    <Flex justifyContent="flex-end" gap={2} pb={3}>
                      {!isReadOnly && (
                        <CreateButton
                          onClick={() => setIsAddingLocationGroup(true)}
                        >
                          Add Location Group
                        </CreateButton>
                      )}

                      <LinkButton
                        href={{
                          pathname: ADMIN_RETAILER_LOCATION_GROUPS_INDEX,
                          // eslint-disable-next-line @typescript-eslint/naming-convention
                          query: { retailer__retailer: selectedRetailerId },
                        }}
                        size="xs"
                        colorScheme="pinkButton"
                        variant="outline"
                      >
                        View in Location Groups ►
                      </LinkButton>
                    </Flex>

                    <RetailerLocationGroupsTable
                      defaultPageSize={25}
                      columns={LOCATION_GROUP_COLUMNS}
                      isAdmin={isAdmin}
                      filter={locationGroupTableFilter}
                      {...FREESTANDING_TABLE_CONTAINER_PROPS}
                      scrollContainerProps={{
                        ...FREESTANDING_TABLE_CONTAINER_PROPS.scrollContainerProps,
                        maxHeight: undefined,
                        minHeight: undefined,
                        bg: "gray.50",
                      }}
                    />
                  </TabPanel>
                )}

                {isAdmin && (
                  <TabPanel
                    px={0}
                    display="flex"
                    flexFlow="column"
                    flex="1 1 100%"
                    minH="25rem"
                  >
                    <AdminRetailerBrandsTable retailerId={selectedRetailerId} />
                  </TabPanel>
                )}

                {isAdmin && (
                  <TabPanel px={0}>
                    {selectedRetailerId && (
                      <RetailerMappings retailerId={selectedRetailerId} />
                    )}
                  </TabPanel>
                )}
              </TabPanels>
            </Tabs>
          </DrawerBody>

          {footer && <DrawerFooter>{footer}</DrawerFooter>}

          {isAdmin && (
            <Drawer
              size="md"
              isOpen={isAddingLocation}
              onClose={() => setIsAddingLocation(false)}
            >
              <CreateRetailerLocation
                isAdmin={isAdmin}
                onCancel={() => setIsAddingLocation(false)}
                onCompleted={() => setIsAddingLocation(false)}
                retailerId={selectedRetailerId ?? undefined}
              />
            </Drawer>
          )}

          {isAdmin && (
            <Drawer
              size="md"
              isOpen={isAddingLocationGroup}
              onClose={() => setIsAddingLocationGroup(false)}
            >
              <CreateRetailerLocationGroup
                isAdmin={isAdmin}
                onCancel={() => setIsAddingLocationGroup(false)}
                onCompleted={() => setIsAddingLocationGroup(false)}
                retailerId={selectedRetailerId ?? undefined}
              />
            </Drawer>
          )}
        </>
      )}
    </DrawerContent>
  );
}
